import { type ReactNode } from "react";
import { cn, formatCategoryDisplay } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { DocumentTagPill } from "../DocumentTagPill";

interface Category {
  id: number;
  name: string;
  name_et?: string | null;
}

interface Discipline {
  id: number;
  name: string;
  name_en?: string | null;
  abbreviation: string | null;
  description?: string | null;
}

interface Stage {
  id: number;
  name: string;
  name_en?: string | null;
  abbreviation: string | null;
}

export interface TimelineDocumentTagsProps {
  /** Document categories */
  categories?: Category[];
  /** Design disciplines */
  disciplines?: Discipline[];
  /** Design stages */
  stages?: Stage[];
  /** Maximum visible tags before "+N" overflow (default: 3) */
  maxVisible?: number;
  /** Callback when the "+N" overflow badge is clicked (e.g. to open tag editor) */
  onOverflowClick?: () => void;
  /** Callback when a tag's (x) remove button is clicked */
  onTagRemove?: (
    tagType: "category" | "discipline" | "stage",
    tagId: number,
    tagName: string
  ) => void;
  /** Optional element rendered inline after the last tag (e.g. (+) button) */
  suffix?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

type TimelineTagType = "category" | "discipline" | "stage";

/**
 * TimelineDocumentTags - Atom component
 *
 * Compact tag display for timeline document cards.
 * Shows categories, disciplines, and stages with overflow indicator.
 * Uses the same outline pill as DocumentTags (`DocumentTagPill`). With no tags
 * it still renders `suffix`, so the tag editor stays reachable.
 */
export function TimelineDocumentTags({
  categories = [],
  disciplines = [],
  stages = [],
  maxVisible = 3,
  onOverflowClick,
  onTagRemove,
  suffix,
  className,
}: TimelineDocumentTagsProps) {
  const { language } = useLanguage();

  // Build unified tag list for display with language-aware label selection
  const allTags: Array<{
    id: string;
    numericId: number;
    label: string;
    type: TimelineTagType;
  }> = [
    ...categories.map((c) => ({
      id: `cat-${c.id}`,
      numericId: c.id,
      label: formatCategoryDisplay(language === "et" ? c.name_et?.trim() || c.name : c.name),
      type: "category" as const,
    })),
    ...disciplines.map((d) => ({
      id: `disc-${d.id}`,
      numericId: d.id,
      label: language === "en" ? d.name_en?.trim() || d.description?.trim() || d.name : d.name,
      type: "discipline" as const,
    })),
    ...stages.map((s) => ({
      id: `stage-${s.id}`,
      numericId: s.id,
      label: language === "en" ? s.name_en?.trim() || s.name : s.name,
      type: "stage" as const,
    })),
  ];

  if (allTags.length === 0 && !suffix) {
    return null;
  }

  const visibleTags = allTags.slice(0, maxVisible);
  const hiddenTags = allTags.slice(maxVisible);
  const overflow = hiddenTags.length;

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {visibleTags.map((tag) => (
        <DocumentTagPill
          key={tag.id}
          label={tag.label}
          tone={tag.type}
          onRemove={onTagRemove ? () => onTagRemove(tag.type, tag.numericId, tag.label) : undefined}
        />
      ))}
      {overflow > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "inline-flex items-center rounded-full border border-border bg-white px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground",
                  onOverflowClick && "cursor-pointer hover:bg-muted"
                )}
                onClick={
                  onOverflowClick
                    ? (e) => {
                        e.stopPropagation();
                        onOverflowClick();
                      }
                    : undefined
                }
              >
                +{overflow}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">{hiddenTags.map((t) => t.label).join(", ")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {suffix}
    </div>
  );
}
